import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PACING_OPTIONS = [
  { id: "slow", label: "Slow", description: "Take things very gradually" },
  { id: "moderate", label: "Moderate", description: "Natural progression" },
  { id: "comfortable", label: "Comfortable", description: "Open to moving at any pace" },
];

const COMMUNICATION_OPTIONS = [
  { id: "few_times_week", label: "A few times a week" },
  { id: "daily", label: "Daily check-ins" },
  { id: "multiple_daily", label: "Multiple times a day" },
];

const CONFLICT_OPTIONS = [
  { id: "discuss_calmly", label: "Discuss calmly right away" },
  { id: "need_space_first", label: "Need space, then discuss" },
  { id: "avoid_conflict", label: "Prefer to avoid confrontation" },
];

const BoundariesPacingStep = ({ onNext }) => {
  const [physicalPacing, setPhysicalPacing] = useState("moderate");
  const [emotionalPacing, setEmotionalPacing] = useState("moderate");
  const [communicationFrequency, setCommunicationFrequency] = useState("daily");
  const [conflictApproach, setConflictApproach] = useState("discuss_calmly");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await api.submitBoundariesPacing({
        physical_pacing: physicalPacing,
        emotional_pacing: emotionalPacing,
        communication_frequency: communicationFrequency,
        conflict_approach: conflictApproach
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Boundaries & Pacing</h2>
      <p className="text-gray-600 mb-6">How you like to build connections</p>

      {/* Physical Pacing */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Physical intimacy pacing
        </label>
        <div className="flex gap-2">
          {PACING_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setPhysicalPacing(option.id)}
              className={`flex-1 px-3 py-3 rounded-xl border-2 text-center transition-all ${
                physicalPacing === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium text-sm ${physicalPacing === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Emotional Pacing */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Emotional openness pacing
        </label>
        <div className="flex gap-2">
          {PACING_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setEmotionalPacing(option.id)}
              className={`flex-1 px-3 py-3 rounded-xl border-2 text-center transition-all ${
                emotionalPacing === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium text-sm ${emotionalPacing === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Communication Frequency */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preferred communication frequency
        </label>
        <div className="space-y-2">
          {COMMUNICATION_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setCommunicationFrequency(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                communicationFrequency === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conflict Approach */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How do you handle disagreements?
        </label>
        <div className="space-y-2">
          {CONFLICT_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setConflictApproach(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                conflictApproach === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default BoundariesPacingStep;
